import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Antispam check
    if (formData.website_url) {
      // Bot detected - simulate silent success
      setStatus({ submitting: false, success: true, error: null });
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter.'
      });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Kunne ikke sende formularen. Prøv igen senere eller ring på +45 22 75 93 58.'
      });
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg border border-slate-200">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1D352E]">
          Få et uforpligtende tilbud
        </h2>
        <p className="text-gray-600 text-sm mt-2">
          Udfyld formularen herunder, så vender Carsten tilbage hurtigst muligt med en uforpligtende snak og pris på din maleropgave.
        </p>
      </div>

      {status.success ? (
        <div className="p-6 bg-[#1D352E]/10 border border-[#1D352E]/20 rounded-xl text-center space-y-3 animate-in fade-in">
          <div className="w-12 h-12 bg-[#1D352E] text-white rounded-full flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-xl font-bold text-[#1D352E]">Tak for din henvendelse!</h3>
          <p className="text-gray-700 text-sm">
            Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt. Du er også altid velkommen til at ringe direkte til Carsten på <a href="tel:22759358" className="font-semibold text-[#65237e] underline nowrap-contact">+45 22 75 93 58</a>.
          </p>
          <button
            type="button"
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-4 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-[#1D352E] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* Honeypot field for antispam */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Hvis du er et menneske, så lad dette felt være tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {status.error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-red-500 shrink-0" />
              <span>{status.error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-1.5">
                Navn <span className="text-[#65237e]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="Dit fulde navn"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#65237e] focus:border-[#65237e] outline-none text-gray-900 text-sm transition-all"
              />
            </div>

            {/* Telefonnummer */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-1.5">
                Telefonnummer <span className="text-[#65237e]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="+45 12 34 56 78"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#65237e] focus:border-[#65237e] outline-none text-gray-900 text-sm transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1.5">
              E-mailadresse <span className="text-[#65237e]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="din@email.dk"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#65237e] focus:border-[#65237e] outline-none text-gray-900 text-sm transition-all"
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-800 mb-1.5">
              Beskriv din maleropgave <span className="text-[#65237e]">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              placeholder="Fortæl kort om hvad der skal males, hvor og hvornår..."
              value={formData.message}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#65237e] focus:border-[#65237e] outline-none text-gray-900 text-sm transition-all resize-y"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full py-3.5 px-6 font-semibold text-white bg-[#65237e] hover:bg-[#521b67] disabled:opacity-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer focus:ring-2 focus:ring-[#65237e] focus:ring-offset-2"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Sender henvendelse...</span>
              </>
            ) : (
              <>
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
